import { TutorReportsHelperService } from '../_helpers/tutor-reports-helper/tutor-reports-helper.service';
import { Component, OnInit, Input, OnChanges, OnDestroy, AfterViewInit, Output, EventEmitter, Optional } from '@angular/core';
import { ActionParams } from '../_models/actions';
import { ActionService } from '../_services/action.service';
import { SubmissionParams } from '../_models/session';
import { Router, NavigationExtras } from "@angular/router";
import { AuthenticationService, SessionService, UserService } from '../_services';

import { ThemeService } from '../_helpers/theming-engine/theme.service';
import { ErrorModalHelperService } from '../_helpers/error-modal-helper/error-modal-helper.service';
import { FeatureHelperService } from '../_helpers/feature-helper/feature-helper.service';
import { ColumnDef, DEFAULT_PAGE_SIZE, PageChange, SortChange, SortDirection } from '../shared/data-table/data-table.model';
import { readRememberedPageSize } from '../shared/data-table/page-size-storage';
import { PagedRequest, PagedResponse } from '../_models/common/paged-response.model';
import { Subject } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { BrandFilterService } from '../_services/brand-filter.service';
import { BrandInfo } from '../_models/reports/brand-info.model';
import { FeatureSwitchService } from '../_services/feature-switch/feature-switch.service';
import { FeatureSwitchKeys } from '../_constants/feature-switch-keys';
import { ACTION_OVERDUE_DAYS } from '../_constants/action-rules';
import { PublicSettingsService } from '../_services/app-settings/public-settings.service';

@Component({
    selector: 'actions-tile',
    templateUrl: 'actions.component.html',
    styleUrls: ['actions.component.css'],
    standalone: false
})

export class ActionsComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() actionDataParams: ActionParams = new ActionParams();
  @Input() submissionParams: SubmissionParams = null;
  @Input() pageheader: string;
  @Input() currDate: string;
  @Output() onFiltered: EventEmitter<any> = new EventEmitter<any>();
  /**
   * True for the dashboard "pending actions" tiles (tutor/coordinator landing pages), which never
   * set `submissionParams` and want today's compact, unpaged tile - not a full paginated table.
   * False (the default) is the actions screen itself (`action-main`), the only host this lane pages.
   */
  @Input() displayall: boolean = false;

  rows: any[] = [];
  columns: ColumnDef[] = [];
  /** The user's brands; the Brand column shows only when there is more than one. */
  brands: BrandInfo[] = [];
  /** The filters.actions switch. Off: no Brand column (and the brand list is never fetched). */
  filtersEnabled: boolean = false;
  /** The overdue threshold, for the Overdue badge's tooltip: served by api/Settings/Public, ACTION_OVERDUE_DAYS as the fallback. */
  public overdueDays: number = ACTION_OVERDUE_DAYS;

  model: any;
  loading: boolean = false;
  noData: boolean = false;
  nodataStr: string = 'There are no pending actions';

  /** Total rows matching the current request, across all pages - only meaningful when `serverSide`. */
  public totalCount: number = 0;
  /**
   * True when this fetch is paged server-side: the actions screen (`!displayall`) with no
   * Client/Tutor/ClientReferrer filter active. False keeps today's exact behaviour - the whole
   * result set fetched and paged/filtered client-side - for the dashboard tiles and for any search
   * with one of those three filters set, since they are only ever evaluated in the browser (§7a).
   */
  public serverSide: boolean = false;
  public pageNumber: number = 1;
  public pageSize: number = DEFAULT_PAGE_SIZE;
  public sortField: string = null;
  public sortDir: SortDirection = 'asc';
  /** Key under which the table remembers the user's page-size choice for the session. */
  public readonly tableId: string = 'actions';

  /** Re-issues the current (browse or search) request; switchMap'd so a rapid page/sort click cannot
   * have an earlier, slower response land after a later one (last-write-wins). */
  private searchRequested: Subject<void> = new Subject<void>();
  private unsubscribe: Subject<void> = new Subject<void>();

  private staffFilter: boolean = false;
  private readOnly: boolean = false;

  constructor(private actionService: ActionService,
    private userService: UserService,
    private router: Router,
    private sessionService: SessionService,
    private tutorReportsHelperService: TutorReportsHelperService, 
    private authService: AuthenticationService,
    public themeService: ThemeService,
    private errorModalHelper: ErrorModalHelperService,
    private featureHelperService: FeatureHelperService,
    private brandFilterService: BrandFilterService,
    private featureSwitches: FeatureSwitchService,
    @Optional() private publicSettings: PublicSettingsService = null
  ) { }

  ngOnInit() {
    // The browse fetch below is issued before the table renders, so the remembered size must be
    // read here - otherwise the request and the footer disagree on page size.
    this.pageSize = readRememberedPageSize(this.tableId) ?? DEFAULT_PAGE_SIZE;

    this.publicSettings?.actionsOverdueDays().pipe(takeUntil(this.unsubscribe)).subscribe(days => this.overdueDays = days);

    let featureList = new Array<string>();
    featureList.push("StaffFilter");

    let currentUserFeatures = this.featureHelperService.checkFeaturePermissions(featureList);

    this.staffFilter = currentUserFeatures.filter(f => f.feature === "StaffFilter")[0].enabled;

    if (this.actionDataParams.header === undefined) {
      this.actionDataParams.header = 'Pending Actions';
    }

    if (this.pageheader != null) {
      this.actionDataParams.header = this.pageheader;
    }

    this.searchRequested.pipe(
      switchMap(() => {
        this.loading = true;
        const paging = this.buildPaging();
        return this.submissionParams
          ? this.actionService.getActionsSearch(this.submissionParams, this.staffFilter, paging)
          : this.actionService.getActions(this.staffFilter, paging);
      }),
      takeUntil(this.unsubscribe)
    ).subscribe({
      next: (data: any) => {
        this.loading = false;
        this.handleResponse(data);
      },
      error: error => {
        this.loading = false;
        this.errorModalHelper.show(error.message, error);
      }
    });

    this.updateServerSideMode();
    // Only the dashboard tiles fetch on their own. The actions screen waits for its search component,
    // which emits the prepopulated date range on load - fetching here as well would show the unfiltered
    // browse until Search was clicked, ignoring the dates already in the filter.
    if (this.submissionParams == null && this.displayall) {
      this.searchRequested.next();
    }

    this.readOnly = this.featureHelperService.getUserFeatures()
      .some(f => f.feature === 'TutorReportsView' && f.enabled) === false;

    this.buildColumns();

    this.featureSwitches.isEnabled(FeatureSwitchKeys.filtersActions).pipe(
      takeUntil(this.unsubscribe),
      filter(enabled => enabled),
      switchMap(() => {
        this.filtersEnabled = true;
        return this.brandFilterService.getMyBrands();
      })
    ).subscribe(brands => {
      this.brands = brands ?? [];
      this.buildColumns();
    });
  }

  private buildColumns(): void {
    const studentHeader = 'Learner';
    const columns: ColumnDef[] = [
      { field: 'createdDate', header: 'Date', type: 'date', width: '12%', sortable: true }
    ];
    // The actions screen only — the dashboard tiles stay compact.
    if (!this.displayall && this.filtersEnabled && this.brands.length > 1) {
      columns.push({ field: 'companyId', header: 'Brand', type: 'template', templateName: 'brand', width: '8%' });
    }
    if (!this.staffFilter) {
      columns.push({ field: 'tutorName', header: 'Tutor', width: '15%' });
    }
    columns.push({ field: 'student', header: studentHeader, type: 'template', templateName: 'student', width: '15%' });
    if (!this.staffFilter) {
      columns.push({ field: 'clientName', header: 'Client', width: '20%' });
    }
    columns.push({ field: 'description', header: 'Task', type: 'template', templateName: 'task' });
    if (this.showCompletedColumn) {
      columns.push({ field: 'completedUtc', header: 'Completed by / at', type: 'template', templateName: 'completed', width: '18%', sortable: true });
    }
    this.columns = columns;
  }

  /**
   * "Completed by / at" shows on the actions screen when the search asks for completed actions
   * (status done or all) — only ever with filters.actions on, since the status is only sent then.
   */
  public get showCompletedColumn(): boolean {
    const status = this.submissionParams?.status;
    return !this.displayall && this.filtersEnabled && (status === 'done' || status === 'all');
  }

  ngAfterViewInit() {

  }

  filterData(data) {
    if (this.submissionParams && this.submissionParams.onSearched) {
      this.nodataStr = 'No Results Found';
    }
    let result = data.map(a => a.placementId);
    if (result.length === 0) {
      this.noData = true;
      return;
    }
    this.noData = false;
    var crmUser = [];
    if (this.submissionParams) {
      if (this.submissionParams.user) {
        this.submissionParams.user.forEach((el) => {
          crmUser.push({ email: el.email, userRelationshipId: el.userRelationshipId });
        });
      }
    }
    else {
      var cUser = this.actionService.currUser;
      if (cUser.userRelationshipId === 9 || cUser.userRelationshipId === 12) {
        crmUser.push({ email: cUser.currentUser.email, userRelationshipId: cUser.userRelationshipId });
      }
    }

    var params: any = {};
    params.placementIds = result;
    params.user = crmUser;

    this.sessionService.getPlacements(params).subscribe((result: any) => {
      var tmpData = [];
      if (result.length > 0) {
        for (var i = 0; i < data.length; i++) {
          let tData = result.filter(x => x.placementID === data[i].placementId)[0];
          if (tData) {
            data[i].clientName = tData.clientName;
            data[i].tutorName = tData.tutorName;
            data[i].tutorId = tData.tutorId;
            data[i].referrerName = tData.referrerName;
            data[i].studentName = tData.studentName;
            data[i].groupName = tData.groupName;
            data[i].isGroupPlacement = tData.isGroup;
            data[i].groupSize = tData.groupSize;
            tmpData.push(data[i]);
          }
        }
        if (this.submissionParams) {
          if (this.submissionParams.client != null) {
            tmpData = tmpData.filter(x => x.clientName.toUpperCase().includes(this.submissionParams.client.toUpperCase()));
          }
          if (this.submissionParams.tutor != null) {
            tmpData = tmpData.filter(x => x.tutorName.toLowerCase().includes(this.submissionParams.tutor.toLowerCase()));
          }
          if (this.submissionParams.clientReferrer != null) {
            tmpData = tmpData.filter(x => x.referrerName !== null && x.referrerName.toUpperCase().includes(this.submissionParams.clientReferrer.toUpperCase()));
          }
          if (this.submissionParams.student != null && this.submissionParams.student.length > 0) {
            var stdList = this.submissionParams.student.split('|');
            var stData = [];
            tmpData.forEach((el) => stdList.forEach((st) => {
              if (el.studentName != null && el.studentName.trim().toUpperCase().includes(st.trim().toUpperCase())) {
                stData.push(el);
              }
            }))
            tmpData = stData;
          }
        }
      }

      //this.onFiltered.emit(tmpData);

      this.rows = tmpData;
      this.noData = this.rows.length === 0;

    }, error => {
      this.errorModalHelper.show(error.message, error);
    })
  }

  ngOnChanges() {
    // this will be executed when clicking the search button
    if (this.submissionParams != null) {
      // A new filter always starts back at page 1 - staying on, say, page 3 of the previous
      // filter's results would silently show the wrong rows for the new one.
      this.pageNumber = 1;
      this.updateServerSideMode();
      // The status decides whether the "Completed by / at" column shows.
      this.buildColumns();
      this.searchRequested.next();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  /**
   * Decides whether this fetch can be paged server-side. Only the actions screen (`!displayall`)
   * qualifies, and only when no Client/Tutor/ClientReferrer filter is active - those three are
   * evaluated in the browser over the whole result set in `filterData`, using the same `!= null`
   * check as here, so paging while one is set would make that filter search only the current page
   * and silently miss matches (Plans/table-lazy-loading.md §7a).
   *
   * A tutor (StaffFilter) never pages server-side either: their search is answered by the API's own-actions
   * path, which always returns the full, bare list with no total. Paging the table server-side then left the
   * total at 0, so the table said there were no pending actions with the rows already fetched (38898).
   */
  private updateServerSideMode(): void {
    const hasUnsupportedFilter = !!this.submissionParams && (
      this.submissionParams.client != null ||
      this.submissionParams.tutor != null ||
      this.submissionParams.clientReferrer != null
    );
    this.serverSide = !this.displayall && !hasUnsupportedFilter && !this.staffFilter;
  }

  /** Current page/sort state as paging parameters, or undefined when this fetch is not server-side. */
  private buildPaging(): PagedRequest {
    if (!this.serverSide) {
      return undefined;
    }
    return {
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      sortField: this.sortField,
      sortDirection: this.sortDir
    };
  }

  /** True when the response is a server-side `PagedResponse<T>` rather than a bare array. */
  private isPagedResponse(value: any): value is PagedResponse<any> {
    return !!value && Array.isArray(value.items) && typeof value.totalCount === 'number';
  }

  /** Handles either wire shape: a bare array (unpaged) or a `PagedResponse<T>` (paged). */
  private handleResponse(data: any): void {
    if (this.isPagedResponse(data)) {
      this.totalCount = data.totalCount;
      if (data.items.length > 0) {
        this.assignData(data.items);
      } else {
        this.rows = [];
        this.noData = true;
      }
      return;
    }

    if (data !== null && data.length > 0) {
      this.assignData(data);
    } else {
      this.rows = [];
      this.noData = true;
    }
  }

  /** A page click or page-size change from the table - re-issues the current request for the new page. */
  public onPageChange(event: PageChange): void {
    this.pageNumber = event.page;
    this.pageSize = event.pageSize;
    this.searchRequested.next();
  }

  /**
   * A column header sort. Only records the new sort state here - the table also emits `pageChange`
   * (reset to page 1) for server-side sorting, and that handler is what re-issues the request.
   */
  public onSortChange(event: SortChange): void {
    this.sortField = event.field;
    this.sortDir = event.direction;
  }

  assignData(data) {
    this.filterData(data);
  }

  onDataclicked(row) {
    if (this.readOnly) {
      return;
    }
    this.actionService.changeMessage(row);
    this.tutorReportsHelperService.setCommonDataBeforeNavigate(row.placementId, null, null, row.id);
    setTimeout(() => {
      this.router.navigate(["/tutorreports", row.placementId]);
    }, 200);
  }
}
